/**
 * Collaboration API Service — интеграция с backend модулем collaboration.
 * Event Master v1.5 — Wave 4 / W4-S2.
 *
 * ADR-002: чтение ответа строго через конверт `{ success, data }` → `data.data`.
 * ADR-004: REST дополняет socket.io-namespace `collaboration` (presence/курсоры/live-комментарии).
 *
 * Здесь же — мапперы backend-сущностей в frontend-типы (`IComment`, `ITeamTemplate`, …),
 * чтобы UI не зависел от формы БД. Те же мапперы переиспользуются для WS-событий
 * (`comment:created`, `reaction:updated`), форма payload совпадает с REST.
 */

import { httpClient } from "../http-client.service";
import { ApiResponse } from "../../types/shared-types";
import {
  IComment,
  ICommentAttachment,
  ITeamTemplate,
  CommentStatus,
} from "../../types/collaboration";

const BASE_URL = "/collaboration";

// ===== BACKEND DTO (форма ответов сервера) =====

/** Тип ресурса на backend (CommentType): widget | dashboard | kpi | filter | general. */
export type BackendCommentType =
  | "widget"
  | "dashboard"
  | "kpi"
  | "filter"
  | "general";

/** Статус комментария на backend (CommentStatus). */
export type BackendCommentStatus =
  | "active"
  | "resolved"
  | "archived"
  | "deleted";

/** Тип реакции на backend (ReactionType). */
export type BackendReactionType =
  | "like"
  | "dislike"
  | "heart"
  | "laugh"
  | "thumbs_up"
  | "thumbs_down"
  | "fire"
  | "rocket"
  | "eyes"
  | "party";

export interface BackendReaction {
  id: string;
  userId: string;
  commentId: string;
  type: BackendReactionType;
  createdAt: string;
}

export interface BackendComment {
  id: string;
  authorId: string;
  type: BackendCommentType;
  targetId: string;
  parentId?: string;
  content: string;
  status: BackendCommentStatus;
  mentions?: string[];
  attachments?: Array<{
    type: "image" | "file" | "link";
    url: string;
    name: string;
    size?: number;
  }>;
  position?: { x: number; y: number; element?: string };
  reactions?: BackendReaction[];
  resolvedBy?: string;
  resolvedAt?: string;
  editedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BackendActiveUser {
  userId: string;
  socketId: string;
  cursor?: { x: number; y: number; element?: string };
  selection?: unknown;
  activeTool?: unknown;
  lastActivity: string;
}

export interface BackendTeamTemplate {
  id: string;
  name: string;
  description?: string;
  category: string;
  tags?: string[];
  thumbnail?: string;
  isPublic?: boolean;
  isOfficial?: boolean;
  rating?: number;
  usageCount?: number;
  downloadCount?: number;
  createdBy: string;
  config?: {
    dashboards?: unknown[];
    widgets?: unknown[];
    filters?: unknown[];
    settings?: unknown;
  };
  createdAt: string;
  updatedAt: string;
}

// ===== МАППЕРЫ =====

/** active | resolved | archived | deleted → open | resolved | archived. */
const mapCommentStatus = (status: BackendCommentStatus): CommentStatus => {
  switch (status) {
    case "resolved":
      return "resolved";
    case "archived":
    case "deleted":
      return "archived";
    case "active":
    default:
      return "open";
  }
};

/**
 * CommentType → resourceType фронта (dashboard | widget | kpi).
 * filter/general на фронте отдельного типа не имеют — сводим к 'dashboard'.
 */
const mapCommentResourceType = (
  type: BackendCommentType,
): IComment["resourceType"] => {
  switch (type) {
    case "widget":
      return "widget";
    case "kpi":
      return "kpi";
    case "dashboard":
    case "filter":
    case "general":
    default:
      return "dashboard";
  }
};

/** Reaction[] → сгруппированный объект { like, dislike, heart, laugh } из userId. */
export const groupReactions = (
  reactions: BackendReaction[] = [],
): IComment["reactions"] => {
  const grouped: IComment["reactions"] = {
    like: [],
    dislike: [],
    heart: [],
    laugh: [],
  };
  for (const reaction of reactions) {
    if (
      reaction.type === "like" ||
      reaction.type === "dislike" ||
      reaction.type === "heart" ||
      reaction.type === "laugh"
    ) {
      grouped[reaction.type].push(reaction.userId);
    }
  }
  return grouped;
};

const mapAttachments = (
  attachments: BackendComment["attachments"] = [],
): ICommentAttachment[] =>
  attachments.map((attachment, index) => {
    const mapped: ICommentAttachment = {
      id: `${attachment.url}_${index}`,
      name: attachment.name,
      type: attachment.type,
      url: attachment.url,
    };
    if (attachment.size !== undefined) {
      return { ...mapped, size: attachment.size };
    }
    return mapped;
  });

/**
 * BackendComment → IComment. Опциональные поля добавляются условно
 * из-за `exactOptionalPropertyTypes` (нельзя присваивать `undefined`).
 */
export const mapComment = (comment: BackendComment): IComment => {
  const base: IComment = {
    id: comment.id,
    resourceType: mapCommentResourceType(comment.type),
    resourceId: comment.targetId,
    authorId: comment.authorId,
    // backend не отдаёт имя автора — UI подставит из справочника/auth, fallback на id
    authorName: comment.authorId,
    content: comment.content,
    mentions: comment.mentions ?? [],
    status: mapCommentStatus(comment.status),
    reactions: groupReactions(comment.reactions),
    attachments: mapAttachments(comment.attachments),
    isEdited: comment.editedAt != null,
    createdAt: new Date(comment.createdAt),
    updatedAt: new Date(comment.updatedAt),
  };

  return {
    ...base,
    ...(comment.parentId != null && { parentId: comment.parentId }),
    ...(comment.position != null && {
      position: {
        x: comment.position.x,
        y: comment.position.y,
        ...(comment.position.element != null && {
          widgetId: comment.position.element,
        }),
      },
    }),
    ...(comment.editedAt != null && { editedAt: new Date(comment.editedAt) }),
    ...(comment.resolvedBy != null && { resolvedBy: comment.resolvedBy }),
    ...(comment.resolvedAt != null && {
      resolvedAt: new Date(comment.resolvedAt),
    }),
  };
};

/** BackendTeamTemplate → ITeamTemplate. */
export const mapTeamTemplate = (
  template: BackendTeamTemplate,
): ITeamTemplate => {
  const base: ITeamTemplate = {
    id: template.id,
    name: template.name,
    description: template.description ?? "",
    category: template.category,
    tags: template.tags ?? [],
    isOfficial: template.isOfficial ?? false,
    isPublic: template.isPublic ?? false,
    dashboardConfig: {
      layout: (template.config?.dashboards as unknown[]) ?? [],
      widgets: (template.config?.widgets as unknown[]) ?? [],
      filters: (template.config?.filters as unknown[]) ?? [],
      settings: template.config?.settings ?? {},
    },
    createdBy: template.createdBy,
    usageCount: template.usageCount ?? 0,
    rating: template.rating ?? 0,
    reviews: [],
    stats: {
      downloads: template.downloadCount ?? 0,
      implementations: template.usageCount ?? 0,
      lastUsed: new Date(template.updatedAt),
    },
    createdAt: new Date(template.createdAt),
    updatedAt: new Date(template.updatedAt),
  };

  return {
    ...base,
    ...(template.thumbnail != null && { thumbnail: template.thumbnail }),
  };
};

// ===== КОММЕНТАРИИ =====

/** GET /collaboration/comments?targetId=&type= */
export const getComments = async (
  targetId: string,
  type: BackendCommentType,
): Promise<IComment[]> => {
  const { data } = await httpClient.get<ApiResponse<BackendComment[]>>(
    `${BASE_URL}/comments`,
    { params: { targetId, type } },
  );
  return (data.data ?? []).map(mapComment);
};

/** POST /collaboration/comments — authorId выводится backend из JWT (XS-008). */
export const createComment = async (payload: {
  type: BackendCommentType;
  targetId: string;
  content: string;
  parentId?: string;
  mentions?: string[];
  position?: { x: number; y: number; element?: string };
}): Promise<IComment> => {
  const { data } = await httpClient.post<ApiResponse<BackendComment>>(
    `${BASE_URL}/comments`,
    payload,
  );
  return mapComment(data.data);
};

/** POST /collaboration/comments/:id/resolve */
export const resolveComment = async (id: string): Promise<IComment> => {
  const { data } = await httpClient.post<ApiResponse<BackendComment>>(
    `${BASE_URL}/comments/${id}/resolve`,
  );
  return mapComment(data.data);
};

/** DELETE /collaboration/comments/:id */
export const deleteComment = async (id: string): Promise<void> => {
  await httpClient.delete(`${BASE_URL}/comments/${id}`);
};

/** POST /collaboration/comments/:id/reactions — возвращает обновлённый список реакций. */
export const toggleReaction = async (
  commentId: string,
  type: BackendReactionType,
): Promise<{
  reactions: IComment["reactions"];
  action: "added" | "removed";
}> => {
  const { data } = await httpClient.post<
    ApiResponse<{ reactions: BackendReaction[]; action: "added" | "removed" }>
  >(`${BASE_URL}/comments/${commentId}/reactions`, { type });
  return {
    reactions: groupReactions(data.data.reactions),
    action: data.data.action,
  };
};

// ===== АКТИВНЫЕ ПОЛЬЗОВАТЕЛИ (presence) =====

/** GET /collaboration/sessions/:resourceType/:resourceId/users */
export const getActiveUsers = async (
  resourceType: string,
  resourceId: string,
): Promise<BackendActiveUser[]> => {
  const { data } = await httpClient.get<ApiResponse<BackendActiveUser[]>>(
    `${BASE_URL}/sessions/${resourceType}/${resourceId}/users`,
  );
  return data.data ?? [];
};

// ===== ШАБЛОНЫ КОМАНД =====

/** GET /collaboration/templates */
export const getTeamTemplates = async (
  filters?: Record<string, unknown>,
): Promise<ITeamTemplate[]> => {
  const { data } = await httpClient.get<ApiResponse<BackendTeamTemplate[]>>(
    `${BASE_URL}/templates`,
    filters ? { params: filters } : undefined,
  );
  return (data.data ?? []).map(mapTeamTemplate);
};

// ===== ПРАВА ДОСТУПА =====

/** Роль фронта → AccessLevel backend. */
export type BackendAccessLevel =
  | "owner"
  | "editor"
  | "commenter"
  | "viewer"
  | "none";

/** POST /collaboration/access — grantedBy выводится backend из JWT. */
export const grantAccess = async (payload: {
  resourceId: string;
  resourceType: string;
  granteeId: string;
  granteeType: "user" | "team";
  level: BackendAccessLevel;
  expiresAt?: string;
}): Promise<{ id: string }> => {
  const { data } = await httpClient.post<ApiResponse<{ id: string }>>(
    `${BASE_URL}/access`,
    payload,
  );
  return data.data;
};

// ===== EXPORT =====

const collaborationApi = {
  getComments,
  createComment,
  resolveComment,
  deleteComment,
  toggleReaction,
  getActiveUsers,
  getTeamTemplates,
  grantAccess,
  // мапперы — для переиспользования в WS-обработчиках
  mapComment,
  mapTeamTemplate,
  groupReactions,
};

export default collaborationApi;
